import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useParams, Link } from "react-router-dom";
import BASE_URL from "../../../../config/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiPlus, FiX, FiEdit2, FiTrash2, FiArrowLeft, FiList } from "react-icons/fi";
import Loader from "../../../../components/Loader";
import { useTranslation } from "react-i18next";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../../components/Popup";
import { cabecerasAuth, claves } from "../../../../queries/cliente";

const TIPO_OPCIONES = [
  "Ratón",
  "Teclado",
  "Pantalla",
  "Impresora",
  "Altavoces",
  "Auriculares",
  "Dock",
  "Webcam",
  "Micrófono",
  "Lector de tarjetas",
  "Hub USB",
  "Cable",
  "Adaptador",
  "Alfombrilla",
  "SAI/UPS",
  "Tablet",
  "Móvil",
  "Otro",
];

const TIPO_ESTADO = ["Activo", "En reparación", "No activo"];

const claseEstado = (estado) => {
  if (estado === "Activo") return "mat-badge-estado-activo";
  if (estado === "En reparación") return "mat-badge-estado-reparacion";
  return "mat-badge-estado-inactivo";
};

const formVacio = (equipoIdParam) => ({
  marca: "",
  modelo: "",
  numero_serie: "",
  tipo: "",
  estado: "Activo",
  notas: "",
  equipoId: equipoIdParam || "",
});

const PerifericosManager = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: equipoIdParam } = useParams();
  const token = localStorage.getItem("AuthToken");
  const tokenInfo = token ? jwtDecode(token) : {};
  const isAdmin = tokenInfo?.role === "admin";


  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState(formVacio(equipoIdParam));
  const [editId, setEditId] = useState(null);

  const [borrarId, setBorrarId] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: token || "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  // Periféricos y equipos comparten caché con el gestor de equipos
  const [consultaPerifericos, consultaEquipos] = useQueries({
    queries: [
      {
        queryKey: claves.perifericos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/perifericos/`, cabecerasAuth())).data || [],
      },
      {
        queryKey: claves.equipos(),
        queryFn: async () => (await axios.get(`${BASE_URL}/equipos/`, cabecerasAuth())).data || [],
      },
    ],
  });

  // Cabecera del equipo cuando se entra desde su ficha (/equipos/:id/perifericos)
  const consultaEquipoHeader = useQuery({
    queryKey: claves.equipo(equipoIdParam),
    queryFn: async () => (await axios.get(`${BASE_URL}/equipos/${equipoIdParam}`, cabecerasAuth())).data || null,
    enabled: Boolean(equipoIdParam),
  });

  useEffect(() => {
    if (consultaPerifericos.isError) toast.error(t('gestorPerifericos.errorObtener'));
  }, [consultaPerifericos.isError, t]);

  useEffect(() => {
    if (consultaEquipos.isError) toast.error(t('gestorPerifericos.errorObtenerEquipos'));
  }, [consultaEquipos.isError, t]);

  useEffect(() => {
    if (consultaEquipoHeader.isError) toast.error(t('gestorPerifericos.errorCargarEquipo'));
  }, [consultaEquipoHeader.isError, t]);

  const perifericos = consultaPerifericos.data || [];
  const equipos = consultaEquipos.data || [];
  const equipoHeader = consultaEquipoHeader.data || null;
  const loading = consultaPerifericos.isPending;

  const fetchPerifericos = () => queryClient.invalidateQueries({ queryKey: claves.perifericos() });

  useEffect(() => {
    setForm((prev) => ({ ...prev, equipoId: equipoIdParam || "" }));
  }, [equipoIdParam]);

  const filtered = useMemo(() => {
    let list = perifericos;
    if (equipoIdParam) {
      list = list.filter((p) => String(p.equipoId) === String(equipoIdParam));
    }
    const q = searchText.toLowerCase();
    return list.filter((p) =>
      [
        p.marca,
        p.modelo,
        p.numero_serie,
        p.tipo,
        p.estado,
        p?.equipo?.modelo,
        p?.equipo?.marca,
        p?.equipo?.numero_serie,
        p.notas,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [perifericos, searchText, equipoIdParam]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const pageItems = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = () => {
    setForm(formVacio(equipoIdParam));
    setEditId(null);
  };

  const cerrarPanel = () => {
    setMostrarForm(false);
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) {
      toast.error(t('gestorPerifericos.accesoRestringido'));
      return;
    }

    try {
      if (editId) {
        await axios.put(`${BASE_URL}/perifericos/${editId}`, form, { headers });
        toast.success(t('gestorPerifericos.actualizadoOk'));
      } else {
        await axios.post(`${BASE_URL}/perifericos/`, form, { headers });
        toast.success(t('gestorPerifericos.creadoOk'));
      }
      await fetchPerifericos();
      cerrarPanel();
    } catch (err) {
      console.error("Error guardando periférico:", err);
      toast.error(t('gestorPerifericos.errorGuardar'));
    }
  };

  const onEdit = (item) => {
    setEditId(item.id);
    setForm({
      marca: item.marca ?? "",
      modelo: item.modelo ?? "",
      numero_serie: item.numero_serie ?? "",
      tipo: item.tipo ?? "",
      estado: item.estado ?? "Activo",
      notas: item.notas ?? "",
      equipoId: (equipoIdParam || item.equipoId || "").toString(),
    });
    setMostrarForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pedirBorrado = (id) => {
    if (!isAdmin) {
      toast.error(t('gestorPerifericos.accesoRestringido'));
      return;
    }
    setBorrarId(id);
  };

  const confirmarBorrado = async () => {
    const id = borrarId;
    setBorrarId(null);
    try {
      await axios.delete(`${BASE_URL}/perifericos/${id}`, { headers });
      toast.success(t('gestorPerifericos.eliminadoOk'));
      await fetchPerifericos();
    } catch (err) {
      console.error("Error al eliminar periférico:", err);
      toast.error(t('gestorPerifericos.errorEliminar'));
    }
  };

  if (!token) {
    return (
      <div className="site-width">
        <p>{t('gestorPerifericos.iniciaSesion')}</p>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" />
      <div className="dashboard-content gap-perifericos">
        <div className="pagina-cabecera">
          <h2>{equipoIdParam ? t('gestorPerifericos.perifericosDelEquipo') : t('gestorPerifericos.gestionPerifericos')}</h2>
          <p>{t('gestorPerifericos.subtitulo')}</p>
        </div>

        {equipoIdParam && equipoHeader && (
          <div className="mat-equipo-banner">
            <div>
              <span className="mat-equipo-banner-label">{t('gestorPerifericos.equipoNum', { id: equipoIdParam })}</span>
              <span className="mat-equipo-banner-titulo">{equipoHeader.marca} {equipoHeader.modelo}</span>
            </div>
            <code className="mat-serie">{equipoHeader.numero_serie || t('gestorPerifericos.sinNumSerie')}</code>
          </div>
        )}
        {!equipoIdParam && (
          <p>{t('gestorPerifericos.subtituloGeneral')}</p>
        )}

        {equipoIdParam && (
          <div className="mat-toolbar-links">
            <Link to="/dashboard/equipos" className="mat-btn-secundario">
              <FiArrowLeft /> {t('gestorPerifericos.volverAEquipos')}
            </Link>
            <Link to="/dashboard/perifericos" className="mat-btn-secundario">
              <FiList /> {t('gestorPerifericos.verTodosPerifericos')}
            </Link>
          </div>
        )}

        {/* Barra de herramientas */}
        <div className="mat-toolbar">
          <input
            type="text"
            placeholder={equipoIdParam ? t('gestorPerifericos.buscarEnEquipo') : t('gestorPerifericos.buscarGeneral')}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
          <div className="mat-toolbar-acciones">
            <select
              className="selector-page-size"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={15}>{t('gestorPerifericos.porPagina', { n: 15 })}</option>
              <option value={30}>{t('gestorPerifericos.porPagina', { n: 30 })}</option>
              <option value={60}>{t('gestorPerifericos.porPagina', { n: 60 })}</option>
            </select>
            <button
              type="button"
              className={`chip-toggle ${mostrarForm ? 'activo' : ''}`}
              onClick={() => (mostrarForm ? cerrarPanel() : setMostrarForm(true))}
            >
              <FiPlus /> {t('gestorPerifericos.nuevoPeriferico')}
            </button>
          </div>
        </div>

        {/* Panel plegable: crear/editar periférico */}
        {mostrarForm && (
          <form className="mat-form-panel" onSubmit={onSubmit}>
            <div className="mat-form-panel-header">
              <h3>
                {editId
                  ? t('gestorPerifericos.editarPerifericoNum', { id: editId })
                  : equipoIdParam
                    ? t('gestorPerifericos.nuevoPerifericoParaEquipo', { id: equipoIdParam })
                    : t('gestorPerifericos.nuevoPeriferico')}
              </h3>
              <button type="button" className="mat-close-btn" onClick={cerrarPanel} aria-label={t('gestorPerifericos.cerrar')}>
                <FiX />
              </button>
            </div>

            <div className="mat-form-grid">
              <div className="mat-campo">
                <label>{t('gestorPerifericos.marca')}</label>
                <input type="text" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorPerifericos.modelo')}</label>
                <input type="text" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorPerifericos.numSerie')}</label>
                <input type="text" value={form.numero_serie} onChange={(e) => setForm({ ...form, numero_serie: e.target.value })} />
              </div>

              <div className="mat-campo">
                <label>{t('gestorPerifericos.tipo')}</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="">{t('gestorPerifericos.seleccionaTipo')}</option>
                  {form.tipo && !TIPO_OPCIONES.includes(form.tipo) && (
                    <option value={form.tipo}>{form.tipo} {t('gestorPerifericos.actual')}</option>
                  )}
                  {TIPO_OPCIONES.map((op) => (
                    <option key={op} value={op}>{t(`gestorPerifericos.tipos.${op}`)}</option>
                  ))}
                </select>
              </div>

              <div className="mat-campo">
                <label>{t('gestorPerifericos.estado')}</label>
                <select value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} required>
                  {form.estado && !TIPO_ESTADO.includes(form.estado) && (
                    <option value={form.estado}>{form.estado} {t('gestorPerifericos.actual')}</option>
                  )}
                  {TIPO_ESTADO.map((estado) => (
                    <option key={estado} value={estado}>{t(`gestorPerifericos.estados.${estado}`)}</option>
                  ))}
                </select>
              </div>

              <div className="mat-campo">
                <label>{t('gestorPerifericos.equipoAsociado')}</label>
                <select value={form.equipoId} onChange={(e) => setForm({ ...form, equipoId: e.target.value })}>
                  <option value="">{t('gestorPerifericos.sinAsignar')}</option>
                  {equipos.map((eq) => (
                    <option key={eq.id} value={eq.id}>
                      #{eq.id} · {eq.marca} {eq.modelo} ({eq.numero_serie || t('gestorPerifericos.sinNumSerie')})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mat-campo mat-campo-full">
                <label>{t('gestorPerifericos.notas')}</label>
                <textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
              </div>
            </div>

            <div className="mat-form-actions">
              <button type="button" className="mat-btn-secundario" onClick={resetForm}>{t('gestorPerifericos.limpiar')}</button>
              <button type="submit" className="mat-btn-primario">
                {editId ? t('gestorPerifericos.actualizarPeriferico') : t('gestorPerifericos.anadirPeriferico')}
              </button>
            </div>
          </form>
        )}

        {/* Listado */}
        {loading ? (
          <div className="loader-fullscreen"><Loader /></div>
        ) : (
          <div className="overflow-tabla">
            <div className="mat-panel">
              <div className="mat-list-header mat-grid-perifericos">
                <span>{t('gestorPerifericos.marcaModelo')}</span>
                <span>{t('gestorPerifericos.numSerie')}</span>
                <span>{t('gestorPerifericos.tipo')}</span>
                <span>{t('gestorPerifericos.estado')}</span>
                <span>{t('gestorPerifericos.equipo')}</span>
                <span>{t('gestorPerifericos.acciones')}</span>
              </div>

              <div className="mat-rows">
                {pageItems.length === 0 ? (
                  <div className="mat-vacio">
                    {equipoIdParam ? t('gestorPerifericos.sinPerifericosEquipo') : t('gestorPerifericos.sinCoincidencias')}
                  </div>
                ) : (
                  pageItems.map((p) => (
                    <div key={p.id} className="mat-row mat-grid-perifericos">
                      <div className="mat-col">
                        <span className="mat-col-titulo">{p.marca} {p.modelo}</span>
                      </div>
                      <div className="mat-col">
                        <code className="mat-serie">{p.numero_serie || '—'}</code>
                      </div>
                      <div className="mat-col">{p.tipo ? t(`gestorPerifericos.tipos.${p.tipo}`, { defaultValue: p.tipo }) : '—'}</div>
                      <div className="mat-col">
                        <span className={`mat-badge-estado ${claseEstado(p.estado)}`}>{t(`gestorPerifericos.estados.${p.estado}`, { defaultValue: p.estado })}</span>
                      </div>
                      <div className="mat-col">
                        {p?.equipo
                          ? `#${p.equipo.id} · ${p.equipo.marca} ${p.equipo.modelo}`
                          : p.equipoId
                            ? `#${p.equipoId}`
                            : "—"}
                      </div>
                      <div className="mat-col mat-col-acciones">
                        <button className="mat-btn-icono" onClick={() => onEdit(p)} title={t('gestorPerifericos.editar')}>
                          <FiEdit2 />
                        </button>
                        <button className="mat-btn-icono mat-btn-icono-danger" onClick={() => pedirBorrado(p.id)} title={t('gestorPerifericos.eliminar')}>
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Paginación */}
        {filtered.length > itemsPerPage && (
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              {t('gestorPerifericos.anterior')}
            </button>
            <span>{t('gestorPerifericos.paginaDe', { actual: currentPage, total: totalPages })}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              {t('gestorPerifericos.siguiente')}
            </button>
          </div>
        )}
      </div>

      <Popup
        open={!!borrarId}
        title={t('gestorPerifericos.eliminarPeriferico')}
        onClose={() => setBorrarId(null)}
        showConfirm={false}
        footerActions={(
          <button className="boton-aceptar-popup" onClick={confirmarBorrado}>{t('gestorPerifericos.eliminar')}</button>
        )}
      >
        <p>{t('gestorPerifericos.confirmarEliminar')}</p>
      </Popup>
    </main>
  );
};

export default PerifericosManager;
